// ============================================================================
// ROLES SERVICE - /api/admin/roles (System Administrator only)
// ============================================================================

import { api } from './api';

export type RoleGroup = 'base' | 'admin' | 'facilitator' | 'integrations' | 'modules' | 'features' | 'custom';

export const ROLE_GROUP_LABELS: Record<RoleGroup, string> = {
  base: 'Base',
  admin: 'Admin',
  facilitator: 'Facilitator',
  integrations: 'Integrations',
  modules: 'Modules',
  features: 'Features',
  custom: 'Custom',
};

export const ROLE_GROUP_ORDER: RoleGroup[] = ['base', 'admin', 'facilitator', 'integrations', 'modules', 'features', 'custom'];

// Fixed in backend/src/authz/roles.seed.ts.
export const SYSTEM_ADMINISTRATOR_ROLE_ID = '2a7d5749-1f37-4219-b326-68e1f1928fdb';
export const GUEST_ROLE_ID = '90fe2f18-7a99-4e97-8ded-d2e7a6a3a3cb';
export const NORMAL_ROLE_ID = '53e259a8-65dc-4237-a34a-a21152890169';

export interface Role {
  id: string;
  name: string;
  parentId?: string;
  actions: string[];
  assignableRoleIds: string[];
  system?: boolean;
  version: number;
  updatedAt: string;
  updatedBy: string;
  holders: number;
  group: RoleGroup;
  seeded: boolean;
  editedSinceSeed: boolean;
}

export interface CatalogueAction {
  id: string;
  area: string;
  description: string;
  write: boolean;
  systemAdministratorOnly: boolean;
}

export interface RoleHolder {
  userId: string;
  displayName: string;
  email: string;
  status: string;
}

export interface RoleDraft {
  name: string;
  parentId?: string | null;
  actions: string[];
  assignableRoleIds: string[];
}

export async function listRoles(): Promise<Role[]> {
  const response = await api.get('/api/admin/roles');
  return response.data.roles;
}

export async function getCatalogue(): Promise<CatalogueAction[]> {
  const response = await api.get('/api/admin/roles/catalogue');
  return response.data.actions;
}

export async function listRoleHolders(roleId: string): Promise<RoleHolder[]> {
  const response = await api.get(`/api/admin/roles/${roleId}/holders`);
  return response.data.holders;
}

export async function createRole(draft: RoleDraft): Promise<Role> {
  const response = await api.post('/api/admin/roles', draft);
  return response.data.role;
}

export async function updateRole(roleId: string, draft: Partial<RoleDraft>, version: number): Promise<Role> {
  const response = await api.put(`/api/admin/roles/${roleId}`, { ...draft, version });
  return response.data.role;
}

export async function deleteRole(roleId: string, version: number): Promise<void> {
  await api.delete(`/api/admin/roles/${roleId}?version=${version}`);
}

/** Replace a user's roles. `expectedRoleIds` (what the editor loaded) turns a concurrent change into a 409. */
export async function setUserRoles(userId: string, roleIds: string[], expectedRoleIds: string[]): Promise<void> {
  await api.put(`/api/admin/users/${userId}/roles`, { roleIds, expectedRoleIds });
}

export function apiErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.error || error?.message || fallback;
}
